import {
  hasMeaningfulAnswerType,
  hasTextAnswer,
  isMinigameType,
} from '@/components/challenges/challengeKinds';
import type { AnswerType } from '@/config/objects/challenges';
import type {
  ChallengeEditorContext,
  ChallengeEditorErrors,
  ChallengeEditorValues,
} from './types';

/** Answer types graded by AI from a media submission instead of text. */
export const AI_VALIDATED_ANSWER_TYPES: readonly AnswerType[] = ['photo', 'video'];

/** Minimum choices for a multiple-choice challenge. */
export const MIN_MULTIPLE_CHOICE_OPTIONS = 2;

/** Hex color (3/4/6/8 digit) — mirrors the API's map-icon color regex. */
export const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** GPS activation radius bounds (meters) — mirrors the API schema. */
export const ACTIVATION_RADIUS_MIN_M = 1;
export const ACTIVATION_RADIUS_MAX_M = 10000;

/** iBeacon major/minor bounds — mirrors the API schema. */
export const IBEACON_VALUE_MAX = 65535;

/**
 * True when the current answer type is AI-validated (photo/video
 * submission graded against `aiContext`).
 */
export function isAiValidatedAnswerType(answerType: AnswerType): boolean {
  return AI_VALIDATED_ANSWER_TYPES.includes(answerType);
}

/**
 * Whether a challenge requires a typed text answer. This is the
 * bug-fix-by-construction: an answer is only required when the type
 * actually supports one (not puzzle/roadblock/minigame) AND the answer
 * type is a text-graded kind (not AI photo/video, not puzzle).
 */
export function requiresTextAnswer(
  type: ChallengeEditorValues['type'],
  answerType: AnswerType,
): boolean {
  if (!hasTextAnswer(type)) return false;
  if (isAiValidatedAnswerType(answerType)) return false;
  // Puzzle answer type is played, not typed. Multiple choice / true-false
  // are handled by their own rules (answer must be one of the options).
  return answerType !== 'puzzle';
}

// ---------------------------------------------------------------------------
// Minigame config validation — mirrors the per-type constraints enforced by
// the API in `apps/hunts/api/src/api/v1/challenges/minigame-config.ts`.
// ---------------------------------------------------------------------------

type MinigameValidator = (config: Record<string, unknown>) => string | undefined;

function numberIn(value: unknown, min: number, max: number): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

function stringLen(value: unknown, min: number, max: number): boolean {
  return typeof value === 'string' && value.trim().length >= min && value.length <= max;
}

function urlList(value: unknown, min: number, max: number): boolean {
  return (
    Array.isArray(value) &&
    value.length >= min &&
    value.length <= max &&
    value.every((v) => typeof v === 'string' && v.trim().length > 0)
  );
}

/**
 * Per-minigame config validators. Each returns an error message when the
 * config violates the server-side limits, or `undefined` when valid.
 * Registry-shaped so adding a minigame is a data edit.
 */
export const MINIGAME_CONFIG_VALIDATORS: Record<string, MinigameValidator> = {
  slot_machine: (c) => {
    if (!urlList(c.reelImages, 2, 20)) return 'Add between 2 and 20 reel image URLs';
    if (!numberIn(c.reelCount, 2, 10)) return 'Reel count must be between 2 and 10';
    if (!numberIn(c.winCondition, 2, 10)) return 'Win condition must be between 2 and 10';
    if (!numberIn(c.pointsOnWin, 0, Number.MAX_SAFE_INTEGER))
      return 'Points on win must be 0 or more';
    if (typeof c.pointsOnLoss !== 'number' || !Number.isInteger(c.pointsOnLoss))
      return 'Points on loss must be a whole number';
    return undefined;
  },
  memory_match: (c) => {
    if (!urlList(c.cardImages, 2, 50)) return 'Add between 2 and 50 card image URLs';
    if (!numberIn(c.gridSize, 2, 8)) return 'Grid size must be between 2 and 8';
    if (!numberIn(c.timeLimit, 0, 3600))
      return 'Time limit must be between 0 and 3600 seconds';
    return undefined;
  },
  guess_word: (c) => {
    if (!stringLen(c.word, 2, 50)) return 'Word must be 2–50 characters';
    if (!stringLen(c.category, 1, 100)) return 'Category is required (max 100 characters)';
    if (!numberIn(c.maxGuesses, 1, 20)) return 'Max guesses must be between 1 and 20';
    return undefined;
  },
  guess_song: (c) => {
    if (!stringLen(c.audioUrl, 1, 2000)) return 'Audio URL is required';
    if (!stringLen(c.songTitle, 1, 200)) return 'Song title is required (max 200 characters)';
    if (!stringLen(c.artist, 1, 200)) return 'Artist is required (max 200 characters)';
    if (!numberIn(c.playDuration, 0, 600))
      return 'Play duration must be between 0 and 600 seconds';
    return undefined;
  },
  related_words: (c) => {
    if (
      !Array.isArray(c.words) ||
      c.words.length < 2 ||
      c.words.length > 30 ||
      !c.words.every((w) => typeof w === 'string' && w.trim().length > 0)
    ) {
      return 'Add between 2 and 30 theme words';
    }
    if (
      !Array.isArray(c.decoyWords) ||
      c.decoyWords.length > 30 ||
      !c.decoyWords.every((w) => typeof w === 'string' && w.trim().length > 0)
    ) {
      return 'Decoy words must be up to 30 non-empty entries';
    }
    if (!stringLen(c.theme, 1, 200)) return 'Theme is required (max 200 characters)';
    return undefined;
  },
};

/**
 * Validate a minigame config for the given type. The server allows a null
 * config (fill it in later), so `null` passes here too — the section's
 * completeness dot reflects the missing config instead.
 */
export function validateMinigameConfig(
  type: string,
  config: Record<string, unknown> | null,
): string | undefined {
  if (!isMinigameType(type) || config === null) return undefined;
  return MINIGAME_CONFIG_VALIDATORS[type]?.(config);
}

// ---------------------------------------------------------------------------
// Full-form validation
// ---------------------------------------------------------------------------

/**
 * Pure validation over the unified editor values. Returns a map of
 * field -> error message; an empty map means the form is saveable.
 *
 * @param values - Current editor values.
 * @param context - Library or event context (event adds gameplay rules).
 * @returns Field-keyed error messages.
 */
export function validate(
  values: ChallengeEditorValues,
  context: ChallengeEditorContext,
): ChallengeEditorErrors {
  const errors: ChallengeEditorErrors = {};

  if (!values.name.trim()) errors.name = 'Name is required';
  if (!values.clue.trim()) errors.clue = 'Clue is required';
  if (values.points < 0) errors.points = 'Points must be 0 or more';
  if (values.hintCost < 0) errors.hintCost = 'Hint cost must be 0 or more';

  // Answer rules — only meaningful when the type supports an answer type.
  if (hasMeaningfulAnswerType(values.type)) {
    if (isAiValidatedAnswerType(values.answerType)) {
      if (!values.aiContext.trim()) {
        errors.aiContext = 'AI validation criteria is required for photo/video challenges';
      }
    } else if (values.answerType === 'multiple_choice') {
      const filled = values.choices.filter((c) => c.trim());
      if (filled.length < MIN_MULTIPLE_CHOICE_OPTIONS) {
        errors.choices = `At least ${MIN_MULTIPLE_CHOICE_OPTIONS} choices are required`;
      }
      if (!values.answer.trim()) {
        errors.answer = 'Please select the correct answer';
      }
    } else if (values.answerType === 'true_false') {
      if (values.answer !== 'true' && values.answer !== 'false') {
        errors.answer = 'Please select True or False';
      }
    } else if (requiresTextAnswer(values.type, values.answerType)) {
      if (!values.answer.trim()) errors.answer = 'Answer is required';
    }
  }

  // Puzzle challenges are played from the challenge image.
  if (values.type === 'puzzle' && !values.imageId.trim()) {
    errors.imageId = 'Puzzle challenges require an image to slice into tiles';
  }

  // Minigame config (allowed to be empty; invalid shapes are rejected).
  const minigameError = validateMinigameConfig(values.type, values.minigameConfig);
  if (minigameError) errors.minigameConfig = minigameError;

  // Location: both-or-neither coordinates.
  if ((values.latitude === null) !== (values.longitude === null)) {
    const missing = values.latitude === null ? 'latitude' : 'longitude';
    errors[missing] = 'Set both latitude and longitude (or clear both)';
  }

  if (context === 'event') {
    if (values.mapIconColor.trim() && !HEX_COLOR_REGEX.test(values.mapIconColor.trim())) {
      errors.mapIconColor = 'Use a hex color like #1F2937';
    }

    if (values.maxCompletions !== null && values.maxCompletions < 1) {
      errors.maxCompletions = 'Max completions must be at least 1';
    }

    // Mirrors the API refinement so the builder sees the problem before
    // submitting rather than as a rejected save.
    if (values.attemptPenaltyPoints !== null && values.attemptPenaltyPercent !== null) {
      errors.attemptPenaltyPercent =
        'Set either a points penalty or a percentage penalty per wrong attempt, not both';
    }

    if (values.maxAttempts !== null && values.maxAttempts < 0) {
      errors.maxAttempts = 'Attempts cannot be negative (0 means unlimited)';
    }

    if (
      values.attemptPenaltyPercent !== null &&
      (values.attemptPenaltyPercent < 0 || values.attemptPenaltyPercent > 100)
    ) {
      errors.attemptPenaltyPercent = 'Percentage penalty must be between 0 and 100';
    }

    // Zero-valued tiers are "empty" rows — dropped by the payload mapper.
    const bonuses = values.speedBonuses.filter((b) => b !== 0);
    if (bonuses.some((b) => !Number.isInteger(b) || b < 1)) {
      errors.speedBonuses = 'Speed bonuses must be whole numbers of 1 or more';
    } else if (bonuses.some((b, i) => i > 0 && b > bonuses[i - 1])) {
      errors.speedBonuses =
        'Speed bonuses must be in descending order (1st place ≥ 2nd place ≥ …)';
    }

    if (values.activationMode === 'gps_proximity') {
      if (values.latitude === null || values.longitude === null) {
        errors.latitude = errors.latitude ?? 'GPS activation requires a pinned location';
      }
      if (
        values.activationRadius === null ||
        values.activationRadius < ACTIVATION_RADIUS_MIN_M ||
        values.activationRadius > ACTIVATION_RADIUS_MAX_M
      ) {
        errors.activationRadius = `Radius must be between ${ACTIVATION_RADIUS_MIN_M} and ${ACTIVATION_RADIUS_MAX_M} meters`;
      }
    }

    if (values.activationMode === 'ibeacon') {
      if (!values.ibeaconUuid.trim()) {
        errors.ibeaconUuid = 'iBeacon activation requires the beacon UUID';
      }
      if (values.ibeaconMajor !== null && !numberIn(values.ibeaconMajor, 0, IBEACON_VALUE_MAX)) {
        errors.ibeaconMajor = `Major must be between 0 and ${IBEACON_VALUE_MAX}`;
      }
      if (values.ibeaconMinor !== null && !numberIn(values.ibeaconMinor, 0, IBEACON_VALUE_MAX)) {
        errors.ibeaconMinor = `Minor must be between 0 and ${IBEACON_VALUE_MAX}`;
      }
    }
  }

  return errors;
}
